import React, { useEffect, useMemo } from 'react';
import FirstStep from 'components/generate-proposal/FirstStep';
import SecondStep from 'components/generate-proposal/SecondStep';
import { BulletTabs } from '@insurancemenu/imenu-components-lib';

import { useHistory } from 'react-router-dom';
import PageHeader from 'components/common/general/layouts/PageHeader';
import GenerateProposalPageStore, {
  GenerateProposalPageStoreContext,
  useGenerateProposalPageStore,
} from 'store/pages/proposal-generation/generateProposalPageStore';
import { observer } from 'mobx-react-lite';
import { useProposalUrlParams } from 'global/hooks/useProposalUrlParams';
import { GENERATE_PROPOSAL_BULLET_TABS } from 'components/generate-proposal/constants';
import ProposalStatusLayout from 'components/common/proposal/ProposalStatusLayout';
import { PageWrapperStyled } from '../../../globalStyles';

const PageContent: React.FC = observer(() => {
  const history = useHistory();
  const { url, cartId, groupId, periodId, catalogId, proposalType } = useProposalUrlParams();

  const {
    currentStepIndex,
    isCurrentControllerValid,
    isLoading,
    completeFlow,
    setIsLoading,
    saveStep,
    preparePage,
    onNavigateStepBack,
  } = useGenerateProposalPageStore();

  async function onNextStep(): Promise<void> {
    await saveStep(catalogId);
  }

  function redirectToReadyPage(): void {
    history.push(`/generate-proposal/ready/${url}&proposalType=${proposalType}`);
  }

  function redirectToChooseTheWayPage(): void {
    history.push(`/generate-proposal/choose-the-way/${url}&proposalType=${proposalType}`);
  }

  async function onComplete(): Promise<void> {
    try {
      await completeFlow(catalogId);
      redirectToReadyPage();
    } catch (error) {
      // This catch ensures the redirect won't happened unless the flow is completed successfully.
      // Actual error handling is located inside the current controller components.
      console.error(error);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void preparePage({ cartId, groupId, enrollmentPeriodId: periodId });
  }, []);

  return (
    <ProposalStatusLayout>
      <PageWrapperStyled>
        <PageHeader title="Proposal Generation" onRedirect={redirectToChooseTheWayPage} />
        <BulletTabs
          title="Step"
          tabs={[<FirstStep key={0} />, <SecondStep key={1} />]}
          onBack={onNavigateStepBack}
          onNext={onNextStep}
          onComplete={onComplete}
          disableNextBtn={!isCurrentControllerValid}
          disableCompleteBtn={!isCurrentControllerValid}
          disableBackBtn={currentStepIndex === 0}
          completeBtnText="Generate"
          isLoading={isLoading}
          data-test-id={GENERATE_PROPOSAL_BULLET_TABS}
        />
      </PageWrapperStyled>
    </ProposalStatusLayout>
  );
});

const GenerateProposalPage: React.FC = () => {
  const store = useMemo(() => new GenerateProposalPageStore(), []);
  return (
    <GenerateProposalPageStoreContext.Provider value={store}>
      <PageContent />
    </GenerateProposalPageStoreContext.Provider>
  );
};

export default GenerateProposalPage;
